import React, { useState, useEffect } from 'react';
import API_BASE_URL from '../../../components/apiconfig/api-config';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FaFileExcel } from 'react-icons/fa';
import * as XLSX from 'xlsx';

const PurchaseSummaryReport = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  
  // Pagination
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 25; // 25 rows per page

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 500);
    return () => clearTimeout(handler);
  }, [search]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [debouncedSearch, page]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/report/purchase-summary-report?page=${page}&limit=${limit}&search=${encodeURIComponent(debouncedSearch)}`, {
        credentials: 'include'
      });
      if (response.ok) {
        const result = await response.json();
        setData(result.data || []);
        setTotal(result.total || 0);
      } else {
        toast.error('Failed to fetch data');
      }
    } catch (error) {
      console.error('Error fetching Purchase Summary Report:', error);
      toast.error('Error fetching Purchase Summary Report');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    if (data.length === 0) {
      toast.info('No data to export');
      return;
    }
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Purchase Summary Report");
    XLSX.writeFile(wb, "Purchase_Order_Summary.xlsx");
  };

  const formatNum = (num, decimals = 2) => {
    if (num === null || num === undefined || num === '-' || isNaN(num)) return '-';
    return Number(num).toLocaleString('en-IN', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="max-w-8xl mx-auto p-3 bg-white rounded-md shadow-sm border border-gray-300">
      <div className="flex flex-col sm:flex-row justify-between items-center pb-2 mb-3 border-b border-gray-200 gap-2">
        <h2 className="text-[15px] font-semibold text-gray-900 tracking-tight">Purchase Order Summary Report</h2>
        <div className="flex gap-1 w-full sm:w-auto">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
              <svg className="h-3.5 w-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 pr-2 py-1.5 border border-gray-300 rounded-sm focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-colors text-[12px] w-full sm:w-56 outline-none text-gray-900 placeholder-gray-500"
            />
          </div>
          <button
            onClick={handleExport}
            className="flex items-center justify-center gap-1.5 bg-[#15803d] text-white px-3 py-1.5 text-[12px] font-semibold rounded-sm hover:bg-[#166534] transition-colors shadow-sm active:translate-y-[1px]"
          >
            <FaFileExcel className="text-[12px]" /> Export
          </button>
        </div>
      </div>

      <div className="overflow-x-auto shadow rounded border border-gray-200">
        <table className="min-w-full text-xs text-left border-collapse whitespace-nowrap">
          <thead className="bg-gray-100 sticky top-0">
            <tr>
              <th className="px-2 py-2 border">S.No</th>
              <th className="px-2 py-2 border">Status</th>
              <th className="px-2 py-2 border">Contract Maker</th>
              <th className="px-2 py-2 border">Purchased From</th>
              <th className="px-2 py-2 border">Buyer</th>
              <th className="px-2 py-2 border">Order Date</th>
              <th className="px-2 py-2 border">Broker</th>
              <th className="px-2 py-2 border">Commodity</th>
              <th className="px-2 py-2 border">Vessel</th>
              <th className="px-2 py-2 border">State</th>
              <th className="px-2 py-2 border">Port</th>
              <th className="px-2 py-2 border">Payment Term</th>
              <th className="px-2 py-2 border">Delivery Date</th>
              <th className="px-2 py-2 border bg-blue-50">Order Qty (MT)</th>
              <th className="px-2 py-2 border bg-blue-50">Billed Qty (MT)</th>
              <th className="px-2 py-2 border bg-blue-50">Unit Price (₹)</th>
              <th className="px-2 py-2 border bg-green-50">Sales Qty (MT)</th>
              <th className="px-2 py-2 border bg-green-50">Sales Rate (₹)</th>
              <th className="px-2 py-2 border bg-green-50">Sold To</th>
              <th className="px-2 py-2 border bg-yellow-50">Pending Sales</th>
              <th className="px-2 py-2 border bg-yellow-50">Pending Billing</th>
              <th className="px-2 py-2 border bg-yellow-50">Pending Days</th>
              <th className="px-2 py-2 border">Total Payment (₹)</th>
              <th className="px-2 py-2 border">Amt Pending (₹)</th>
              <th className="px-2 py-2 border bg-purple-50">Brokerage (₹)</th>
              <th className="px-2 py-2 border bg-purple-50 font-bold">Profit (₹)</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="26" className="text-center py-4">Loading...</td></tr>
            ) : data.length > 0 ? (
              data.map((row, index) => (
                <tr key={index} className="hover:bg-gray-50 border-b">
                  <td className="px-2 py-1.5 border text-center">{(page - 1) * limit + index + 1}</td>
                  <td className="px-2 py-1.5 border">{row.Status}</td>
                  <td className="px-2 py-1.5 border">{row.Contract_Maker}</td>
                  <td className="px-2 py-1.5 border">{row.Purchased_from}</td>
                  <td className="px-2 py-1.5 border max-w-[150px] truncate" title={row.Buyer}>{row.Buyer || '-'}</td>
                  <td className="px-2 py-1.5 border">{row.Order_Date || '-'}</td>
                  <td className="px-2 py-1.5 border">{row.Broker}</td>
                  <td className="px-2 py-1.5 border">{row.Commodity}</td>
                  <td className="px-2 py-1.5 border">{row.Vessel || '-'}</td>
                  <td className="px-2 py-1.5 border">{row.State || '-'}</td>
                  <td className="px-2 py-1.5 border">{row.Port || '-'}</td>
                  <td className="px-2 py-1.5 border">{row.Payment_Term || '-'}</td>
                  <td className="px-2 py-1.5 border">{row.Delivery_Date || '-'}</td>
                  <td className="px-2 py-1.5 border text-right font-semibold bg-blue-50/30">{formatNum(row.Order_Qty, 3)}</td>
                  <td className="px-2 py-1.5 border text-right bg-blue-50/30">{formatNum(row.Billed_Qty, 3)}</td>
                  <td className="px-2 py-1.5 border text-right bg-blue-50/30">{formatNum(row.Unit_Price)}</td>
                  <td className="px-2 py-1.5 border text-right font-semibold text-green-700 bg-green-50/30">{formatNum(row.Sales_Qty, 3)}</td>
                  <td className="px-2 py-1.5 border text-right text-green-700 bg-green-50/30">{formatNum(row.Sales_Rate)}</td>
                  <td className="px-2 py-1.5 border max-w-[150px] truncate bg-green-50/30" title={row.Sold_to}>{row.Sold_to || '-'}</td>
                  <td className="px-2 py-1.5 border text-right bg-yellow-50/30 font-semibold">{formatNum(row.Pending_Sales, 3)}</td>
                  <td className="px-2 py-1.5 border text-right bg-yellow-50/30">{formatNum(row.Pending_Billing, 3)}</td>
                  <td className={`px-2 py-1.5 border text-center font-bold bg-yellow-50/30 ${row.Pending_Days < 0 ? 'text-red-600' : 'text-gray-700'}`}>{row.Pending_Days}</td>
                  <td className="px-2 py-1.5 border text-right">{formatNum(row.Total_Payment)}</td>
                  <td className="px-2 py-1.5 border text-right text-red-600 font-semibold">{formatNum(row.Amt_Pending)}</td>
                  <td className="px-2 py-1.5 border text-right bg-purple-50/30">{formatNum(row.Brokerage)}</td>
                  <td className={`px-2 py-1.5 border text-right font-bold bg-purple-50/50 ${row.Profit >= 0 ? 'text-green-700' : 'text-red-600'}`}>
                    {formatNum(row.Profit)}
                  </td>
                </tr>
              ))
            ) : (
              <tr><td colSpan="26" className="text-center py-4">No Data Found</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row justify-between items-center mt-3 text-[12px]">
          <div className="text-gray-600 mb-2 sm:mb-0">
            Showing <span className="font-semibold text-gray-900">{((page - 1) * limit) + 1}</span> to <span className="font-semibold text-gray-900">{Math.min(page * limit, total)}</span> of <span className="font-semibold text-gray-900">{total}</span> records
          </div>
          <div className="inline-flex shadow-sm rounded-sm">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1 border border-gray-300 bg-white text-gray-800 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed font-medium rounded-l-sm transition-colors"
            >
              Previous
            </button>
            <div className="px-3 py-1 border-t border-b border-gray-300 bg-gray-50 text-gray-900 font-medium">
              {page} / {totalPages}
            </div>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-3 py-1 border border-gray-300 bg-white text-gray-800 hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed font-medium rounded-r-sm transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}
      <ToastContainer />
    </div>
  );
};

export default PurchaseSummaryReport;
